import React from "react";
import { ChangeEvent, FormEvent, Fragment } from "react";
import ReactModal from "react-modal";
import { Prompt } from "react-router";
import { RouteComponentPropsHistory } from "../../types";
import { Link } from "react-router-dom";

import HasRight from "../HasRight";
import NoteBox from "../NoteBox";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";
import ViewToolbar from "../ViewToolbar";

import { V2, apiDelete, apiGet, apiPost, apiPut } from "../../Api";
import { dateFromLocalISOString, formatDateLocal, formatDateTimeLocal, formatName, formatPhone } from "../../Utilities";
import { IAuthProps, WellKnownGroups } from "../AuthContext";
import withAuth from "../withAuth";
import withTitle from "../withTitle";

import "../../blocks.css";
import "../../button.css";
import "../../form.css";
import "../../icomoon.css";
import "../../list.css";
import "../../modal.css";
import "../../spinner.css";
import "../viewer.css";


interface ICreateAppointmentState {
    appointment: any;
    isEditingNote: boolean;
    isLoading: boolean | string;

    secondaryError?: string;
    statusMessage?: string;
    isLoadingSecondary: boolean;

    isFromSurvey: boolean; // Is showing from creating at a health fair

    dialogNurse: string;
    dialogNurses: any[];
    isModalOpen: boolean;

    isDeletionModalOpen: boolean;
    deletionModalLoading: boolean | string;

    isContactModalOpen: boolean;
    contactModalLoading: boolean | string;
    contactNote: string;
    contactFiles: string[];

    [name: string]: any;
}

class AppointmentView extends React.Component<RouteComponentPropsHistory<any, any> & IAuthProps, ICreateAppointmentState> {
    private contactNoteRef: React.RefObject<NoteBox> = React.createRef();

    constructor(props: Readonly<RouteComponentPropsHistory<any, any> & IAuthProps>) {
        super(props);

        this.state = {
            appointment: undefined,
            isEditingNote: false,
            isLoading: true,
            isLoadingSecondary: false,

            isFromSurvey: this.props.location.state && this.props.location.state.isFromSurvey,

            dialogNurse: "",
            dialogNurses: [],
            isModalOpen: false,

            deletionModalLoading: false,
            isDeletionModalOpen: false,

            isContactModalOpen: false,
            contactModalLoading: "",
            contactNote: "",
            contactFiles: []
        };

        this.submitNote = this.submitNote.bind(this);
        this.submitContactNote = this.submitContactNote.bind(this);
        this.markAsCreated = this.markAsCreated.bind(this);
        this.markAsCanceled = this.markAsCanceled.bind(this);
        this.markAsNoShow = this.markAsNoShow.bind(this);
        this.markAsComplete = this.markAsComplete.bind(this);
        this.markAsSaved = this.markAsSaved.bind(this);
        this.dismissAppointment = this.dismissAppointment.bind(this);
        this.closeDialog = this.closeDialog.bind(this);
        this.finishMarkAsComplete = this.finishMarkAsComplete.bind(this);
        this.formInputChanged = this.formInputChanged.bind(this);
        this.newSurgery = this.newSurgery.bind(this);
        this.noteEditorChange = this.noteEditorChange.bind(this);
        this.deleteItem = this.deleteItem.bind(this);
        this.realDeleteItem = this.realDeleteItem.bind(this);
        this.closeDeletionModal = this.closeDeletionModal.bind(this);
        this.getStrippedAppointment = this.getStrippedAppointment.bind(this);
        this.showContactDialog = this.showContactDialog.bind(this);
        this.closeContactModal = this.closeContactModal.bind(this);
        this.setContactStatus = this.setContactStatus.bind(this);
        this.handleContactNote = this.handleContactNote.bind(this);
        this.handleContactNoteChange = this.handleContactNoteChange.bind(this);
    }

    public async componentDidMount() {
        this.loadAppointment();
    }

    public render() {
        if (this.state.appointment === false) {
            return (
                <div className="viewer-root">
                    <span className="error-text">Appointment not found</span>
                </div>
            );
        }
        if (this.state.isLoading === true) {
            return (
                <div className="viewer-root">
                    <div className="spinner-center lds-dual-ring" />
                </div>
            );
        }
        else if (typeof this.state.isLoading === "string") {
            return (
                <div className="viewer-root">
                    <span className="error-text">{this.state.isLoading}</span>
                </div>
            );
        }

        if (!this.state.appointment) {
            return (
                <div className="viewer-root">
                    <span className="error-text">Could not load appointment</span>
                </div>
            );
        }

        return (
            <div className="viewer-root with-actionbar">
                <ViewToolbar>
                    <ToolbarMenuItem right="appt:write">
                        <Link to={{ pathname: this.props.location.pathname + "/edit", state: { appointment: this.getStrippedAppointment() } }} className="icon-pencil" title="Reschedule" />
                    </ToolbarMenuItem>
                    <OverflowMenuItem>
                        <ToolbarMenuItem condition={this.state.appointment.isChanged && this.state.appointment.status !== 0} right="appt:write">
                            <button type="button" onClick={this.markAsSaved}><i className="icon-check" /><span>Changes applied</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem condition={this.state.appointment.status > 1} right="appt:write">
                            <button type="button" onClick={this.markAsCreated}><i className="icon-check" /><span>Reset to Active</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem condition={this.state.appointment.status === 0} right="appt:write">
                            <button type="button" onClick={this.markAsCreated}><i className="icon-check" /><span>Mark as Created</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem condition={this.state.appointment.status === 1} right="appt:write">
                            <button type="button" onClick={this.markAsCanceled}><i className="icon-times" /><span>Mark as Canceled</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem condition={this.state.appointment.status === 1} right="appt:write">
                            <button type="button" onClick={this.markAsNoShow}><i className="icon-times" /><span>Mark as No Show</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem condition={this.state.appointment.status === 1} right="appt:write">
                            <button type="button" onClick={this.markAsComplete}><i className="icon-check" /><span>Mark as Arrived</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem right="appt:delete">
                            <button type="button" className="red" onClick={this.deleteItem}><i className="icon-trash" /><span>Delete appointment</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem right="appt:contact" condition={!this.props.authContext.serverType}>
                            <button type="button" onClick={this.showContactDialog}><i className="icon-clock-o" /><span>Contacted</span></button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                {this.state.isModalOpen
                    ? <ReactModal
                        isOpen={this.state.isModalOpen} onRequestClose={this.closeDialog}
                        overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-portal"
                        bodyOpenClassName="modal-open"
                        contentLabel="Appointment Completion">
                        <form className="input-form" onSubmit={this.finishMarkAsComplete}>
                            <section className="info-block">
                                <header>
                                    <h3>Complete Appointment</h3>
                                </header>

                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-dialog-nurse">Provider</label>
                                        <select id="frm-dialog-nurse" name="dialogNurse" value={this.state.dialogNurse} onChange={this.formInputChanged} autoComplete="off_please_chrome">
                                            {this.state.dialogNurses.map(i => <option key={i.id} value={i.id}>{formatName(i.name)}</option>)}
                                        </select>
                                    </div>
                                </section>
                                <br />
                                <div className="action-panel-dialog">
                                    <button type="button" className="button btn-light push-right" onClick={this.closeDialog} disabled={this.state.isLoadingSecondary}>Cancel</button>
                                    <button type="submit" className="button" disabled={this.state.isLoadingSecondary}>Complete</button>
                                </div>
                            </section>
                        </form>
                    </ReactModal>
                    : null}
                {this.state.isDeletionModalOpen
                    ? <ReactModal
                        isOpen={this.state.isDeletionModalOpen} onRequestClose={this.closeDeletionModal}
                        overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-deletion-portal"
                        bodyOpenClassName="modal-deletion-open"
                        contentLabel="Confirm Delete">
                        <form className="input-form" onSubmit={this.realDeleteItem}>
                            <section className="info-block">
                                <header>
                                    <h3>Confirm Delete</h3>
                                </header>

                                <span>Are you sure you want to delete this appointment?</span>
                                <p>{formatName(this.state.appointment.patient.name)}<br /><small>{formatDateTimeLocal(this.state.appointment.appointmentTimestamp)}</small></p>
                                {
                                    this.state.deletionModalLoading === true
                                        ? <div className="spinner lds-dual-ring" />
                                        : (typeof this.state.deletionModalLoading) === "string"
                                            ? <span className="error-text">{this.state.deletionModalLoading}</span>
                                            : ""
                                }
                                &nbsp;
                            <div className="action-panel-dialog">
                                    <button type="submit" className="button red push-right">Yes, Delete</button>
                                    <button type="button" className="button" onClick={this.closeDeletionModal}>No</button>
                                </div>
                            </section>
                        </form>
                    </ReactModal>
                    : null}
                {this.state.isContactModalOpen
                    ? <ReactModal
                        isOpen={this.state.isContactModalOpen} onRequestClose={this.closeContactModal}
                        overlayClassName="modal-overlay" className="modal-content modal-larger" portalClassName="modal-billing-portal"
                        bodyOpenClassName="modal-billing-open"
                        contentLabel="Contacted dialog">
                        <form className="input-form">
                            <section className="info-block">
                                <header>
                                    <h3>Contacted patient</h3>
                                </header>

                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-contact-note">Proof of contact <small>(note is optional)</small></label>
                                        <NoteBox ref={this.contactNoteRef} defaultNoteOpen={true} allowUpload={true} requireUpload={true} uploadCompress="image" hideLoading={true} stayOpenAfterSubmit={true} mode="single" onNewNote={this.handleContactNote} onNewNoteChange={this.handleContactNoteChange} />
                                        <small><b>Note:</b> You must upload at least one proof of contact</small>
                                    </div>
                                </section>
                                {
                                    this.state.contactModalLoading === true
                                        ? <div className="spinner lds-dual-ring" />
                                        : (typeof this.state.contactModalLoading) === "string"
                                            ? <span className="error-text">{this.state.contactModalLoading}</span>
                                            : ""
                                }
                                &nbsp;
                                <div className="action-panel-dialog">
                                    <div className="spring fill-space" />
                                    <button type="button" className="button green" onClick={() => this.setContactStatus("reached")}>Reached</button>
                                    <button type="button" className="button yellow" onClick={() => this.setContactStatus("notreached")}>Did not reach</button>
                                    <button type="button" className="button" onClick={this.closeContactModal}>Close</button>
                                </div>
                            </section>
                        </form>
                    </ReactModal>
                    : null}
                <Prompt when={this.state.isEditingNote} message="Your changes will not be saved if you continue." />
                {this.state.secondaryError ? <div className="error-box">{this.state.secondaryError}</div> : ""}
                {this.state.isLoadingSecondary ? <div className="spinner-overlay"><div className="spinner lds-dual-ring-big" /></div> : ""}
                <div className="column-root">
                    <section className="info-block">
                        <header>
                            <h3>Patient Information</h3>
                        </header>
                        {/*<div className="column-root">*/}
                        <dl className="info-list">
                            <div className="info-list-row">
                                <div>
                                    <dt>Name</dt>
                                    <dd>{formatName(this.state.appointment.patient.name)}</dd>
                                </div>
                                <div>
                                    <dt>Date of Birth</dt>
                                    <dd>{formatDateLocal(dateFromLocalISOString(this.state.appointment.patient.birthDate) || "")}</dd>
                                </div>
                            </div>
                            <div className="info-list-row">
                                <div>
                                    <dt>Phone</dt>
                                    <dd><a href={`tel:${this.state.appointment.patient.phone}`}>{formatPhone(this.state.appointment.patient.phone)}</a></dd>
                                </div>
                                <div>
                                    <dt>Email Address</dt>
                                    <dd><a href={`mailto:${this.state.appointment.patient.email}`}>{this.state.appointment.patient.email}</a></dd>
                                </div>
                            </div>

                            <div className="info-list-row">
                                <div className="col-50">
                                    <dt>Insurance Provider</dt>
                                    <dd>{this.state.appointment.patient.insuranceProvider ? this.state.appointment.patient.insuranceProvider : "N/A"}</dd>
                                </div>
                                <div className="col-25">
                                    <dt>Sex</dt>
                                    <dd>{this.sexToString(this.state.appointment.patient.sex)}</dd>
                                </div>
                                <div className="col-25">
                                    <dt>Chart #</dt>
                                    <dd>{this.state.appointment.patient.chartNumber || "Unspecified"}</dd>
                                </div>
                            </div>

                            {
                                this.state.appointment.questionnaireId
                                    ? <Fragment>
                                        <dt>Source</dt>
                                        <dd>
                                            {this.state.appointment.fair ? "Questionnaire." : "Nurse lead."} <HasRight right="survey:read"><Link to={`/surveys/${this.state.appointment.questionnaireId}`} className="button small btn-narrow btn-inline btn-outline">View</Link></HasRight>
                                        </dd>
                                        {this.state.appointment.fair
                                            ? <>
                                                <dd><small>{this.state.appointment.fair.name}</small></dd>
                                                <dd><small>{formatDateTimeLocal(this.state.appointment.fair.scheduledDate)}</small></dd>
                                            </>
                                            : null}
                                    </Fragment>
                                    : <Fragment>
                                        <dt>Source</dt>
                                        <dd>Standalone</dd>
                                    </Fragment>
                            }
                        </dl>

                        {/*</div>*/}
                    </section>
                    <section className="info-block">
                        <header>
                            <h3>Appointment Summary</h3>
                        </header>
                        <dl className="info-list">
                            <dt>Office Location</dt>
                            <dd>{this.state.appointment.location.name}</dd>
                            <dd>
                                <small>
                                    {this.state.appointment.location.address.street} {`${this.state.appointment.location.address.city}, ${this.state.appointment.location.address.state} ${this.state.appointment.location.address.zip}`}
                                </small>
                            </dd>
                            <div className="info-list-row col-60-40">
                                <div>
                                    <dt>Appointment Date &amp; Time</dt>
                                    <dd>{formatDateTimeLocal(this.state.appointment.appointmentTimestamp)}</dd>
                                </div>
                                <div>
                                    <dt>Appointment Type</dt>
                                    <dd>{this.state.appointment.appointmentType.name}</dd>
                                </div>
                            </div>
                            <dt>Appointment Status</dt>
                            <dd>{this.statusToText(this.state.appointment.status)}</dd>
                            <dt>Scheduled By</dt>
                            <dd>{formatName(this.state.appointment.employee.name)}</dd>
                            <div className="info-list-row col-60-40">
                                <div>
                                    <dt>Provider</dt>
                                    <dd>{this.state.appointment.nurse ? formatName(this.state.appointment.nurse.name) : "N/A"}</dd>
                                </div>
                                {
                                    this.state.appointment.appointmentType.type === "uro"
                                        ? <div>
                                            <dt>Technician</dt>
                                            <dd>{this.state.appointment.technician ? formatName(this.state.appointment.technician.name) : "N/A"}</dd>
                                        </div>
                                        : null
                                }
                            </div>

                            {this.state.appointment.appointmentType.type !== "pellet"
                                ? <div className="info-list-row col-60-40">
                                    <div>
                                        <dt>Likely candidate for surgery?</dt>
                                        <dd>{this.state.appointment.isSurgeryCandidate ? "Yes" : "No"}</dd>
                                    </div>
                                    <div>
                                        <dt>Suggested surgery</dt>
                                        <dd>{this.state.appointment.surgeryEstimate ? this.state.appointment.surgeryEstimate : "N/A"}</dd>
                                    </div>
                                </div>
                                : <div>
                                    <dt>Is first visit?</dt>
                                    <dd>{this.state.appointment.pelletFirstVisit ? "Yes" : "No"}</dd>
                                </div>}
                        </dl>
                    </section>
                </div>
                &nbsp;
                <div className="column-root">
                    <section className="info-block">
                        <header>
                            <h3>Appointment History</h3>
                        </header>
                        <NoteBox notes={this.state.appointment.events} hideControls={true} />
                    </section>
                    <div className="row-root">
                        <section className="info-block">
                            <header>
                                <h3>Notes</h3>
                            </header>
                            <NoteBox displayName={this.props.authContext.user.displayName} notes={this.state.appointment.notes} onNewNote={this.submitNote} hideControls={!this.props.authContext.hasRight("appt:write")} onEditorChange={this.noteEditorChange} />
                        </section>
                        <HasRight right="appt:contact">
                            <section className="info-block">
                                <header>
                                    <h3>Contact Notes</h3>
                                </header>
                                <NoteBox displayName={this.props.authContext.user.displayName} notes={this.state.appointment.contactNotes} onNewNote={this.submitContactNote} allowUpload={true} uploadCompress="image" hideControls={!this.props.authContext.hasRight("appt:contact")} onEditorChange={this.noteEditorChange} />
                            </section>
                        </HasRight>
                    </div>
                </div>
                &nbsp;
                <div className="action-panel">
                    <button type="button" className="button push-right" onClick={this.dismissAppointment}>Ok</button>
                    {
                        this.state.appointment.surgeryId
                            ? <HasRight right="surgery:read">
                                <Link to={`/surgery/${this.state.appointment.surgeryId}`} className="button text-with-icon"><i className="icon-calendar" /><span>View Surgery</span></Link>
                            </HasRight>
                            : this.state.appointment.appointmentType.type !== "pellet"
                                ? <HasRight right="surgery:create">
                                    <button type="button" className="button text-with-icon wrap-text" onClick={this.newSurgery}><i className="icon-calendar-plus-o" /><span>Schedule Surgery</span></button>
                                </HasRight>
                                : null
                    }
                </div>
            </div>
        );
    }

    private async submitNote(body: string) {
        const note = {
            body
        };

        const noteResult = await apiPost<any>(`appointments/${this.state.appointment.id}/notes`, note);
        noteResult.author = formatName(noteResult.employee.name);

        this.setState(s => {
            s.appointment.notes.push(noteResult);

            return {
                appointment: s.appointment,
            }
        });
    }

    private async submitContactNote(body: string, files?: string[]) {
        const note = {
            body,
            files
        };

        const noteResult = await apiPost<any>(`appointments/${this.state.appointment.id}/notes/contact`, note);
        noteResult.author = formatName(noteResult.employee.name);

        this.setState(s => {
            s.appointment.contactNotes.push(noteResult);

            return {
                appointment: s.appointment,
            }
        });
    }

    private noteEditorChange(isEditing: boolean) {
        this.setState({
            isEditingNote: isEditing
        });
    }

    private renderEventBody(a: any) {
        switch (a.type) {
            case 1: return "Appointment scheduled";
            case 2: return "Appointment created";
            case 3: return "Patient didn't show up";
            case 4: return "Appointment canceled";
            case 5: {
                return (
                    <Fragment>
                        <div>Appointment rescheduled</div>
                        {
                            a.valueDate1 && a.valueDate2
                                ? <div>
                                    <br />From <strong>{formatDateTimeLocal(a.valueDate1)}</strong>
                                    <br />To <strong>{formatDateTimeLocal(a.valueDate2)}</strong>
                                </div>
                                : ""
                        }
                    </Fragment>
                );
            }
            case 6: return "Patient arrived";
            case 7: return +a.valueNumeric === 1 ? "Patient information updated" : `Unknown update type: ${a.valueNumeric}`;
            case 8: return "Appointment reset to active";
            case 9: return +a.valueNumeric === 2 ? "Contacted and reached patient" : +a.valueNumeric === 1 ? "Contacted and didn't reach patient" : `Contact: ${a.valueNumeric}`;
            default: return a.valueText || (a.valueNumeric ? a.valueNumeric.toString() : `UNKNOWN EVENT TYPE ${a.type}. Please report this`);
        }
    }

    private async loadAppointment() {
        try {
            const appt = await apiGet<any>(`appointments/${this.props.match.params.id}`);

            // Add implicit "appointment scheduled" event
            if (appt.events) {
                appt.events.unshift({
                    employee: appt.employee,
                    employeeId: appt.employeeId,
                    timestamp: appt.timestamp,
                    type: 1,
                    attachments: appt.proofFileId
                        ? [
                            {
                                fileId: appt.proofFileId,
                                title: "Proof"
                            }
                        ]
                        : []
                });
            }

            if (appt.notes) {
                appt.notes
                    .sort((a: any, b: any) => a.timestamp.localeCompare(b.timestamp))
                    .forEach((a: any) => a.author = formatName(a.employee.name));
            }

            if (appt.contactNotes) {
                appt.contactNotes
                    .sort((a: any, b: any) => a.timestamp.localeCompare(b.timestamp))
                    .forEach((a: any) => a.author = formatName(a.employee.name));
            }

            if (appt.events) {
                appt.events.forEach((a: any) => {
                    a.author = formatName(a.employee.name);
                    a.body = this.renderEventBody(a);
                });
            }

            // Load providers that apply to the current appointment type
            const nurses = await apiGet<any[]>(`employees/summary?group=${WellKnownGroups.Provider}`);
            nurses.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)));
            nurses.unshift({ id: "", name: { first: "", last: "" } });

            this.setState({
                appointment: appt,
                dialogNurses: nurses,
                isLoading: false,
            });
        }
        catch (e) {
            if (e.statusCode === 404) {
                this.setState({
                    appointment: false,
                    isLoading: false,
                });
            }

            this.setState({
                appointment: undefined,
                isLoading: e.message || e || "",
            });
        }
    }

    private async markAsCreated() {
        await this.changeStatus("active");
    }

    private async markAsCanceled() {
        await this.changeStatus("canceled");
    }

    private async markAsNoShow() {
        await this.changeStatus("noshow");
    }

    private markAsComplete() {
        this.setState({
            isModalOpen: true
        });
    }

    private async finishMarkAsComplete(e: FormEvent) {
        e.preventDefault();

        await this.changeStatus("complete", { nurseId: this.state.dialogNurse });
    }

    private async changeStatus(status: string, extra?: any) {
        try {
            this.setState({
                isLoadingSecondary: true,
                secondaryError: undefined,
            });

            let sendData = { status };
            if (extra) {
                sendData = Object.assign(sendData, extra);
            }

            await apiPut(`appointments/${this.state.appointment.id}/status`, sendData);

            this.props.history.goBack();
        }
        catch (e) {
            this.setState({
                isLoadingSecondary: false,
                secondaryError: `Error while updating appointment: ${e.message}`,
            });
        }
    }

    private async markAsSaved() {
        try {
            this.setState({
                isLoadingSecondary: true,
                secondaryError: undefined,
            });

            await apiPost(`appointments/${this.state.appointment.id}/finished`);

            this.setState({
                isLoadingSecondary: false,
                statusMessage: undefined,
            });

            this.props.history.goBack();
        }
        catch (e) {
            this.setState({
                isLoadingSecondary: false,
                secondaryError: `Error while updating appointment: ${e.message}`,
            });
        }
    }

    private statusToText(status: number) {
        switch (status) {
            case 0: return "Pending";
            case 1: return "Active";
            case 2: return "Canceled";
            case 3: return "No Show";
            case 4: return "Complete";
            case 5: return "Pending Reschdule";
            case 6: return "Pending Update";
            default: return `Unknown: ${status}`;
        }
    }

    private dismissAppointment() {
        this.props.history.go(this.state.isFromSurvey ? -3 : -1);
    }

    private closeDialog() {
        this.setState({
            isModalOpen: false
        });
    }

    private formInputChanged(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.value;

        if (e.currentTarget.type === "number") {
            this.setState({
                [name]: parseInt(value, 10)
            });
        }
        else {
            this.setState({
                [name]: value
            });
        }
    }

    private async newSurgery() {
        try {
            if (this.state.appointment.patient.chartNumber) {
                // Appointment has a chart number listed, attempt to find the patient
                const result = await V2.get<any>(`patients?chart=${this.state.appointment.patient.chartNumber}`);
                if (result.id) {
                    this.props.history.push("/surgery/create", {
                        patientId: result.id,
                        appointmentId: this.state.appointment.id
                    });
                    return;
                }
            }

            // Chart not found, send user to patient search page
            this.props.history.push("/patient/search", {
                action: "create_surgery_from_appointment",
                data: {
                    appointmentId: this.state.appointment.id
                }
            });
        }
        catch (e) {

        }
    }

    private deleteItem() {
        this.setState({
            isDeletionModalOpen: true
        });
    }

    private closeDeletionModal() {
        this.setState({
            isDeletionModalOpen: false
        });
    }

    private async realDeleteItem(e?: FormEvent) {
        if (e) {
            e.preventDefault();
        }

        try {
            this.setState({
                deletionModalLoading: true
            });

            await apiDelete(`appointments/${this.state.appointment.id}`);

            this.setState({
                deletionModalLoading: false
            });

            this.props.history.goBack();
        }
        catch (e) {
            this.setState({
                deletionModalLoading: e.message || e
            });
        }
    }

    private getStrippedAppointment() {
        const stripped: any = {};
        Object.assign(stripped, this.state.appointment);

        delete stripped.events;

        return stripped;
    }

    private sexToString(sex: string) {
        switch (sex) {
            case "f":
            case "F": return "Female";
            case "m":
            case "M": return "Male";
            case null:
            case undefined: return "Unspecified";
            default: return `Unknown (${sex})`;
        }
    }

    private showContactDialog() {
        this.setState({
            isContactModalOpen: true
        });
    }

    private closeContactModal() {
        this.setState({
            isContactModalOpen: false
        });
    }

    private async setContactStatus(status: string) {
        if (!this.contactNoteRef.current) {
            return;
        }

        try {
            this.setState({
                contactModalLoading: true
            });

            let statusNumber = 0;
            switch (status) {
                case "notreached": statusNumber = 1; break;
                case "reached": statusNumber = 2; break;
                default: throw new Error("Unknown status code");
            }

            // If we have file IDs already, don't attempt to re-upload
            if (this.state.contactFiles.length === 0) {
                // Process note and upload files
                if (!await this.contactNoteRef.current.submit()) {
                    this.setState({
                        contactModalLoading: false,
                    });
                    return;
                }
            }

            const data = {
                status: statusNumber,
                note: this.state.contactNote,
                attachments: this.state.contactFiles
            };

            await V2.post(`appointments/${this.state.appointment.id}/contact`, data);

            this.setState({
                contactModalLoading: false,
                isContactModalOpen: false,
                contactNote: "",
                contactFiles: [],
            }, () => this.props.history.goBack());
        }
        catch (e) {
            this.setState({
                contactModalLoading: e.message || e || "Could not set status"
            });
        }
    }

    private async handleContactNote(body: string, files?: string[]) {
        this.setState({
            contactNote: body,
            contactFiles: files || []
        });
    }

    private handleContactNoteChange(body: string) {
        this.setState({
            contactNote: body
        });
    }
}

export default withTitle("Appointment Details", withAuth(AppointmentView));
